/**
 * Data hooks for §9 Campaigns.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as campaignsApi from '../../api/campaigns.api.js';
import * as masterDataApi from '../../api/masterData.api.js';
import * as usersApi from '../../api/users.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';

/** @param {object} [params] e.g. `{ q, status, owner_id, campaign_type_id }` */
export function useCampaigns(params = {}) {
  return useQuery({
    queryKey: queryKeys.campaigns.list(params),
    queryFn: () => campaignsApi.listCampaigns(params),
    placeholderData: (previous) => previous,
  });
}

export function useCampaign(id, enabled = true) {
  return useQuery({
    queryKey: queryKeys.campaigns.detail(id),
    queryFn: () => campaignsApi.getCampaign(id),
    enabled: Boolean(id) && enabled,
  });
}

/** The picker every "Add to Campaign" flow needs — active/planned campaigns first is left to the caller. */
export function useCampaignPickerOptions() {
  return useQuery({
    queryKey: queryKeys.campaigns.list({ limit: 200, sort: 'name' }),
    queryFn: () => campaignsApi.listCampaigns({ limit: 200, sort: 'name' }),
    staleTime: 60_000,
  });
}

/** Dropdown options the create/edit form needs: campaign type and owner. */
export function useCampaignFormOptions() {
  const types = useQuery({
    queryKey: queryKeys.masterData.options('campaign_types'),
    queryFn: () => masterDataApi.listRecords('campaign_types', { limit: 100, is_active: 'true' }),
    staleTime: 300_000,
  });
  const users = useQuery({
    queryKey: queryKeys.users.list({ limit: 200, picker: true }),
    queryFn: () => usersApi.listUsers({ limit: 200 }),
    staleTime: 300_000,
  });

  const typeRows = types.data?.data ?? [];
  const userRows = users.data?.data ?? [];
  const loading = types.isLoading || users.isLoading;

  return useMemo(() => ({
    types: typeRows.map((t) => ({ value: t.id, label: t.name })),
    owners: userRows.map((u) => ({ value: u.id, label: u.full_name ?? u.email })),
    loading,
  }), [typeRows, userRows, loading]);
}

export function useCampaignMembers(id, params = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.campaigns.members(id, params),
    queryFn: () => campaignsApi.listCampaignMembers(id, params),
    enabled: Boolean(id) && enabled,
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload) => campaignsApi.createCampaign(payload),
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all });
      dispatch(pushToast({ tone: 'success', message: `Campaign "${campaign.name}" created.` }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not create that campaign', detail: error.message }));
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, patch, rowVersion }) => campaignsApi.updateCampaign(id, patch, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all });
      dispatch(pushToast({ tone: 'success', message: 'Campaign saved.' }));
    },
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : 'Could not save that change',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, reason, rowVersion }) => campaignsApi.deleteCampaign(id, { reason, rowVersion }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all });
      dispatch(pushToast({ tone: 'success', message: 'Campaign deleted.' }));
    },
    onError: (error) => {
      const denied = error?.status === 403;
      dispatch(pushToast({
        tone: 'danger',
        message: denied ? "You don't have permission to delete campaigns" : 'Could not delete that campaign',
        detail: denied ? 'Ask an administrator, or set the campaign to Cancelled instead.' : error.message,
      }));
    },
  });
}

/**
 * Add leads/contacts to a campaign. Success is reported even when some rows
 * were skipped (already a member, opted out) — those are the API's normal,
 * documented outcomes, not partial failures.
 */
export function useAddCampaignMembers() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, payload }) => campaignsApi.addCampaignMembers(id, payload),
    onSuccess: (result) => {
      /* `campaigns.all` is `['campaigns']` — React Query's default fuzzy
         matching invalidates every key starting with it, members lists
         included, so there's no need to also list every params shape a
         members query might be cached under. */
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all });
      const skipped = result.skipped_duplicates + result.skipped_opted_out;
      dispatch(pushToast({
        tone: 'success',
        message: `Added ${result.added}${skipped ? `, skipped ${skipped}` : ''} to the campaign.`,
      }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not add to that campaign', detail: error.message }));
    },
  });
}

export function useRemoveCampaignMember() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ campaignId, memberId }) => campaignsApi.removeCampaignMember(campaignId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all });
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not remove that member', detail: error.message }));
    },
  });
}
